// CharacterManager.tsx - Component for managing saved characters
import React, { useState } from "react";
import {
	Users,
	Plus,
	Trash2,
	Edit,
	Save,
	X,
	Download,
	Upload,
	AlertCircle,
	Clock,
} from "lucide-react";

interface CharacterManagerProps {
	characters: {
		id: number;
		name: string;
		level: number;
		class: string;
		race: string;
		updatedAt: string;
	}[];
	currentCharacterId: number | null;
	onLoadCharacter: (id: number) => void;
	onDeleteCharacter: (id: number) => void;
	onCreateNew: () => void;
	onSaveCharacter: () => Promise<number>;
	onExport: () => void;
	onImport: (event: React.ChangeEvent<HTMLInputElement>) => void;
	isLoading: boolean;
	currentCharacterName: string;
}

export const CharacterManager: React.FC<CharacterManagerProps> = ({
	characters,
	currentCharacterId,
	onLoadCharacter,
	onDeleteCharacter,
	onCreateNew,
	onSaveCharacter,
	onExport,
	onImport,
	isLoading,
	currentCharacterName,
}) => {
	const [isOpen, setIsOpen] = useState(false);
	const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
	const [saveLoading, setSaveLoading] = useState(false);

	const handleSave = async () => {
		try {
			setSaveLoading(true);
			await onSaveCharacter();
		} catch (error) {
			console.error("Failed to save character:", error);
		} finally {
			setSaveLoading(false);
		}
	};

	const handleDelete = async (id: number) => {
		try {
			await onDeleteCharacter(id);
			setDeleteConfirm(null);
		} catch (error) {
			console.error("Failed to delete character:", error);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (!isOpen) {
		return (
			<div className="fixed top-4 right-4 z-50">
				<button
					onClick={() => setIsOpen(true)}
					className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200 shadow-lg"
				>
					<Users size={18} />
					Characters ({characters.length})
				</button>
			</div>
		);
	}

	return (
		<div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
			<div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
				{/* Header */}
				<div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<Users size={24} />
							<h2 className="text-2xl font-bold">
								Character Manager
							</h2>
						</div>
						<button
							onClick={() => setIsOpen(false)}
							className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
						>
							<X size={20} />
						</button>
					</div>
				</div>

				{/* Action Buttons */}
				<div className="p-6 border-b border-gray-200">
					<div className="flex flex-wrap gap-3">
						<button
							onClick={onCreateNew}
							className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
						>
							<Plus size={16} />
							New Character
						</button>

						<button
							onClick={handleSave}
							disabled={saveLoading}
							className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<Save size={16} />
							{saveLoading ? "Saving..." : "Save Current"}
						</button>

						<button
							onClick={onExport}
							className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
						>
							<Download size={16} />
							Export Current
						</button>

						<label className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors duration-200 cursor-pointer">
							<Upload size={16} />
							Import Character
							<input
								type="file"
								accept=".json"
								onChange={onImport}
								className="hidden"
							/>
						</label>
					</div>

					{currentCharacterId && (
						<div className="mt-3 text-sm text-gray-600">
							Currently editing:{" "}
							<span className="font-semibold">
								{currentCharacterName || "Unnamed Character"}
							</span>
						</div>
					)}
				</div>

				{/* Character List */}
				<div className="flex-1 overflow-y-auto p-6">
					{isLoading ? (
						<div className="flex items-center justify-center py-12">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
							<span className="ml-3 text-gray-600">
								Loading characters...
							</span>
						</div>
					) : characters.length === 0 ? (
						<div className="text-center py-12">
							<Users
								size={48}
								className="mx-auto text-gray-400 mb-4"
							/>
							<h3 className="text-lg font-semibold text-gray-600 mb-2">
								No Characters Yet
							</h3>
							<p className="text-gray-500 mb-4">
								Create your first character to get started!
							</p>
							<button
								onClick={onCreateNew}
								className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-200"
							>
								Create First Character
							</button>
						</div>
					) : (
						<div className="grid gap-4">
							{characters.map((char) => (
								<div
									key={char.id}
									className={`p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
										currentCharacterId === char.id
											? "border-indigo-500 bg-indigo-50"
											: "border-gray-200 bg-white hover:border-gray-300"
									}`}
								>
									<div className="flex items-center justify-between">
										<div className="flex-1">
											<div className="flex items-center gap-3 mb-2">
												<h3 className="text-lg font-semibold text-gray-800">
													{char.name ||
														"Unnamed Character"}
												</h3>
												{currentCharacterId ===
													char.id && (
													<span className="px-2 py-1 bg-indigo-600 text-white text-xs rounded-full">
														Current
													</span>
												)}
											</div>
											<div className="flex items-center gap-4 text-sm text-gray-600">
												<span>Level {char.level}</span>
												{char.race && (
													<span>{char.race}</span>
												)}
												{char.class && (
													<span>{char.class}</span>
												)}
											</div>
											<div className="flex items-center gap-1 text-xs text-gray-500 mt-2">
												<Clock size={12} />
												<span>
													Updated{" "}
													{formatDate(char.updatedAt)}
												</span>
											</div>
										</div>

										<div className="flex items-center gap-2 ml-4">
											<button
												onClick={() =>
													onLoadCharacter(char.id)
												}
												className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
												title="Load Character"
											>
												<Edit size={16} />
											</button>

											{deleteConfirm === char.id ? (
												<div className="flex items-center gap-2">
													<button
														onClick={() =>
															handleDelete(
																char.id
															)
														}
														className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 transition-colors duration-200"
													>
														Confirm
													</button>
													<button
														onClick={() =>
															setDeleteConfirm(
																null
															)
														}
														className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400 transition-colors duration-200"
													>
														Cancel
													</button>
												</div>
											) : (
												<button
													onClick={() =>
														setDeleteConfirm(
															char.id
														)
													}
													className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
													title="Delete Character"
												>
													<Trash2 size={16} />
												</button>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Footer */}
				<div className="border-t border-gray-200 p-4 bg-gray-50">
					<div className="flex items-center justify-between text-sm text-gray-600">
						<div className="flex items-center gap-2">
							<AlertCircle size={16} />
							<span>
								Characters are automatically saved to your
								browser's database
							</span>
						</div>
						<button
							onClick={() => setIsOpen(false)}
							className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-200"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};
